'use client';

import { NextIntlClientProvider } from 'next-intl';
import { ReactNode, useEffect, useState, createContext, useContext, useCallback } from 'react';
import { locales, defaultLocale, type Locale } from './config';

// Static imports for all locale messages (required for Next.js build)
import enMessages from '../../../locale/en.json';
import zhHansMessages from '../../../locale/zh-Hans.json';
// import zhHantMessages from '../locale/zh-Hant.json';

// Map locale codes to their message objects
const localeMessages: Record<Locale, any> = {
  'en': enMessages,
  'zh-Hans': zhHansMessages,
//   'zh-Hant': zhHantMessages,
};

const LANGUAGE_STORAGE_KEY = 'app-language-preference';

// Context for locale state management
const LocaleContext = createContext<{
  locale: Locale;
  setLocale: (locale: Locale) => void;
} | null>(null);

/**
 * Detects browser language and returns the best matching supported language code
 */
function detectBrowserLanguage(): Locale {
  if (typeof window === 'undefined') {
    return defaultLocale;
  }

  // Get browser languages (priority order)
  const browserLanguages = navigator.languages || [navigator.language];
  
  // Check each browser language in priority order
  for (const browserLang of browserLanguages) {
    // Direct match (e.g., "en" matches "en")
    if (locales.includes(browserLang as Locale)) {
      return browserLang as Locale;
    }
    
    // Check language code without region (e.g., "en-US" -> "en")
    const langCode = browserLang.split('-')[0];
    if (locales.includes(langCode as Locale)) {
      return langCode as Locale;
    }
    
    // Check for Simplified/Traditional Chinese variants
    if (langCode === 'zh') {
      // Prefer Simplified Chinese for zh-CN, Traditional for zh-TW
      if (browserLang.toLowerCase().includes('tw') || browserLang.toLowerCase().includes('hk')) {
        // return 'zh-Hant';
        return 'zh-Hans';
      }
      return 'zh-Hans'; // Default to Simplified
    }
  }
  
  return defaultLocale;
}

/**
 * Gets initial language from storage or browser detection
 */
function getInitialLanguage(): Locale {
  if (typeof window === 'undefined') {
    return defaultLocale;
  }

  // Check localStorage first
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && locales.includes(stored as Locale)) {
    return stored as Locale;
  }

  // Fall back to browser detection
  return detectBrowserLanguage();
}

interface LocaleProviderProps {
  children: ReactNode;
}

export function LocaleProvider({ children }: LocaleProviderProps) {
  // Initialize with default locale and messages for SSR compatibility
  const [locale, setLocaleState] = useState<Locale>(defaultLocale);
  const [messages, setMessages] = useState<any>(localeMessages[defaultLocale]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Function to update locale and reload messages
  const updateLocale = useCallback((newLocale: Locale) => {
    if (!locales.includes(newLocale)) {
      console.warn(`Unsupported locale: ${newLocale}. Falling back to ${defaultLocale}`);
      newLocale = defaultLocale;
    }

    setLocaleState(newLocale);
    
    // Persist to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLocale);
      document.documentElement.lang = newLocale;
    }

    // Load messages from static imports
    const newMessages = localeMessages[newLocale] || localeMessages[defaultLocale];
    setMessages(newMessages);
  }, []);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') {
      return;
    }

    // Initialize locale from storage or browser detection
    const initialLocale = getInitialLanguage();
    updateLocale(initialLocale);
    setIsInitialized(true);
  }, [updateLocale]);

  // Listen for locale changes from other tabs
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === LANGUAGE_STORAGE_KEY && e.newValue) {
        const newLocale = e.newValue as Locale;
        if (locales.includes(newLocale)) {
          updateLocale(newLocale);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [updateLocale]);

  // Always provide NextIntlClientProvider, even during SSR
  // This ensures useTranslations hooks work during prerendering
  return (
    <LocaleContext.Provider value={{ locale, setLocale: updateLocale }}>
      <NextIntlClientProvider locale={locale} messages={messages}>
        {children}
      </NextIntlClientProvider>
    </LocaleContext.Provider>
  );
}

/**
 * Hook to get and set locale (replaces LanguageContext)
 */
export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within LocaleProvider');
  }
  return context;
}

